import React, { useEffect } from 'react'
import { useParams } from 'react-router'
import { MeasureDataContext } from '../contexts/MeasureDataProvider'
import {
  getMeasureFromUrl,
  getUnitObjectFromUnitURLForMeasure,
  isValidUnitToUnitURLForMeasure
} from '../utils/measureHelpers'
import { Measure, Unit, ConversionResult } from '../types'
import { AppContext } from '../contexts/AppProvider'
import { Box, Grid, useTheme } from '@mui/material'
import MeasureTabsBar from '../components/MeasureTabsBar'
import SingleTabContent from '../components/SingleTabContent'
import { convertValueFromUnitToUnit } from '../utils/converterUtils'
import Big from 'big.js'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { DIRECTION, Direction, urlParamQuantityToConvert } from '../constants'
import { useAppNavigate } from '../hooks/useAppNavigate'
import SEOHelmet from '../components/SEOHelmet'
import { generateConverterPageSEOProps } from '../utils/seo'

const ConverterPage: React.FC = () => {
  const { navigateToNotFound } = useAppNavigate()
  const theme = useTheme()

  const { measure: newMeasureUrl, fromUnit: newFromUnitUrl, toUnit: newToUnitUrl } = useParams()
  const [searchParams] = useSearchParams()
  const q = searchParams.get(urlParamQuantityToConvert) // if not present => null

  const { allMeasures } = React.useContext(MeasureDataContext)
  const { appState, setAppState } = React.useContext(AppContext)
  const { currentMeasure } = appState
  const { fromUnit, toUnit } = appState.tabs[currentMeasure.codeName]

  const handleConversion = (
    valueToConvert: string,
    fromUnit: Unit,
    toUnit: Unit,
    measure: Measure,
    sourceDirection: Direction
  ): ConversionResult => {
    try {
      if (!valueToConvert) {
        return {
          fromNumber: '',
          toNumber: '',
          isValid: false
        }
      }

      const convertedValue = convertValueFromUnitToUnit(
        measure,
        new Big(valueToConvert),
        sourceDirection === DIRECTION.FROM ? fromUnit : toUnit, // Switch units based on direction
        sourceDirection === DIRECTION.FROM ? toUnit : fromUnit
      )

      // Return values in correct order based on direction
      return sourceDirection === DIRECTION.FROM
      ? {
          fromNumber: valueToConvert,
          toNumber: convertedValue.toString(),
          isValid: true
        }
      : {
          fromNumber: convertedValue.toString(),
          toNumber: valueToConvert,
          isValid: true
        }
    } catch (e) {
      console.error('Conversion error:', e)
      if (searchParams.has(urlParamQuantityToConvert)) {
        toast.error(`Invalid conversion number from shared link (${q}).`, { autoClose: 3500 })
      }

      // Return empty state in correct order based on direction
      return sourceDirection === DIRECTION.FROM
      ? {
          fromNumber: valueToConvert,
          toNumber: '',
          isValid: false
        }
      : {
          fromNumber: '',
          toNumber: valueToConvert,
          isValid: false
        }
    }
  }


  useEffect(() => {
    if (newMeasureUrl === undefined || newFromUnitUrl === undefined || newToUnitUrl === undefined) {
      return navigateToNotFound()
    }

    if (!isValidUnitToUnitURLForMeasure(allMeasures, newMeasureUrl, newFromUnitUrl, newToUnitUrl)) {
      return navigateToNotFound()
    }

    const newCurrentMeasure: Measure = getMeasureFromUrl(allMeasures, newMeasureUrl)
    const newFromUnit: Unit = getUnitObjectFromUnitURLForMeasure(newCurrentMeasure, newFromUnitUrl)
    const newToUnit: Unit = getUnitObjectFromUnitURLForMeasure(newCurrentMeasure, newToUnitUrl)

    if (currentMeasure !== newCurrentMeasure || fromUnit !== newFromUnit || toUnit !== newToUnit || q !== null) {
      // safeguard to do conversion otherwise, back button in browser would break app upon going back on a form with an invalid number
      let valueToConvert
      try {
        valueToConvert = q !== null
          ? decodeURIComponent(q)
          : appState.tabs[newCurrentMeasure.codeName].fromNumber
      } catch (error) {
        console.error('Error decoding shared value:', error)
        valueToConvert = appState.tabs[newCurrentMeasure.codeName].fromNumber
        toast.error(`Cannot decode value ${urlParamQuantityToConvert} from url (${q}).`)
      }
      const { fromNumber, toNumber, isValid } = handleConversion(valueToConvert, newFromUnit, newToUnit, newCurrentMeasure, DIRECTION.FROM)

      setAppState({
        ...appState,
        currentMeasure: newCurrentMeasure,
        tabs: {
          ...appState.tabs,
          [newCurrentMeasure.codeName]: {
            ...appState.tabs[newCurrentMeasure.codeName],
            fromUnit: newFromUnit,
            toUnit: newToUnit,
            fromNumber,
            toNumber,
            isConversionValid: isValid
          }
        }
      })
    }
  }, [newMeasureUrl, newFromUnitUrl, newToUnitUrl, q])

  return (
    <>
    <SEOHelmet {...generateConverterPageSEOProps(currentMeasure, fromUnit, toUnit)} />
    <Box sx={{ backgroundColor: theme.palette.custom.backgroundWhisperPurple, minHeight: 'calc(100vh - 151px)' }}>
      <Grid container justifyContent="center" >
        <Grid item xs={12}
          style={{
            backgroundColor: 'white',
            paddingLeft: 'calc((100vw - 1300px) / 2)',
            paddingRight: 'calc((100vw - 1300px) / 2)',
            width: 'calc(100% - 10px)' // WIDTH - MARGIN
          }}>
            <MeasureTabsBar />
        </Grid>
        <Grid item xs={12} sm={9} md={7} lg={7} xl={6}
          style={{
            backgroundColor: 'white',
            borderLeft: '1px solid #e0e0e0',
            borderRight: '1px solid #e0e0e0',
            boxShadow: '2px 0px 6px -6px black, -2px 0px 6px -6px black',
            maxWidth: '900px',
            minHeight: 'calc(100vh - 224px)'
          }}>
          <SingleTabContent onConversion={handleConversion} />
        </Grid>
      </Grid>
    </Box>
    </>
  )
}

export default ConverterPage
